import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Avatar,
  Divider,
  Alert,
  CircularProgress,
  useTheme,
} from '@mui/material';
import {
  Edit as EditIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  Schedule as ScheduleIcon,
  School as SchoolIcon,
  Favorite as FavoriteIcon,
  ShieldOutlined as ShieldOutlinedIcon,
  MapOutlined as MapOutlinedIcon,
  PeopleOutline as PeopleOutlineIcon,
  StarOutline as StarOutlineIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { caregiverProfileService } from '../services/caregiverProfileService';
import {
  caregiverOnboardingService,
  CaregiverOnboardingProgress,
} from '../services/caregiverOnboardingService';
// FEAT-061 — Trust & Safety acknowledgement gate (MVP-8.20…8.27).
import { trustSafetyService } from '../services/trustSafetyService';
import BioManagementSidebar from '../components/sidebars/BioManagementSidebar';
import ServicesManagementSidebar from '../components/sidebars/ServicesManagementSidebar';
import AvailabilityManagementSidebar from '../components/sidebars/AvailabilityManagementSidebar';
import CertificationsManagementSidebar from '../components/sidebars/CertificationsManagementSidebar';
import ProfileCompletionMeter from '../components/ProfileCompletionMeter';
import ProfileSummary from '../components/caregiver/ProfileSummary';
import CredentialDocumentUploader from '../components/CredentialDocumentUploader';
import StateAgencyRegisterLink from '../components/StateAgencyRegisterLink';
import SkillSelector from '../components/SkillSelector';

interface CaregiverProfile {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  avatar: string | null;
  bio: string | null;
  phoneNumber: string | null;
  location: string | null;
  yearsExperience: number | null;
  hourlyRate: number | null;
  responseTime: string | null;
  languages: string[];
  profileComplete: boolean;
  stateCode: string | null;
  stateLicenseNumber: string | null;
  services: Service[];
  availability: Availability[];
  certifications: Certification[];
  specialties: Specialty[];
}

interface Service {
  id: string;
  name: string;
  description: string | null;
  duration: number | null;
  price: number | string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Availability {
  id: string;
  dayOfWeek: string;
  startTime: string;
  endTime: string;
  isAvailable: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Certification {
  id: string;
  name: string;
  issuer: string;
  issueDate: string | null;
  expiryDate: string | null;
  credentialId: string | null;
  verified: boolean;
  documentUrl: string | null;
  documentName: string | null;
  createdAt: string;
  updatedAt: string;
}

interface Specialty {
  id: string;
  name: string;
  description: string | null;
}

const CaregiverProfileManagementPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { state } = useApp();
  const user = state.user;

  const [profile, setProfile] = useState<CaregiverProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSidebar, setActiveSidebar] = useState<string | null>(null);
  // FEAT-045 — onboarding progress drives the warm "Continue sign-up"
  // banner. We treat a load failure as "no banner" rather than a hard
  // error so the existing profile management surface still renders for
  // returning caregivers.
  const [onboarding, setOnboarding] = useState<CaregiverOnboardingProgress | null>(null);
  // FEAT-061 — show the Trust & Safety gate banner until the caregiver has
  // acknowledged the current content version. A load failure means "no
  // banner" so the surface still renders for returning caregivers.
  const [trustSafetyAcknowledged, setTrustSafetyAcknowledged] = useState<boolean | null>(null);

  useEffect(() => {
    if (user?.role === 'CAREGIVER') {
      fetchProfile();
      fetchOnboarding();
      fetchTrustSafety();
    }
  }, [user]);

  const fetchTrustSafety = async () => {
    try {
      const status = await trustSafetyService.getAcknowledgement();
      setTrustSafetyAcknowledged(status.acknowledged);
    } catch {
      // Never block the page on a transient failure — hide the banner.
      setTrustSafetyAcknowledged(null);
    }
  };

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const profileData = await caregiverProfileService.getProfile();
      setProfile(profileData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchOnboarding = async () => {
    try {
      const progress = await caregiverOnboardingService.getProgress();
      setOnboarding(progress);
    } catch {
      // Don't surface a banner if the call fails — the API might be
      // mid-deploy. The user is not blocked.
      setOnboarding(null);
    }
  };

  const handleSidebarOpen = (sidebarType: string) => {
    setActiveSidebar(sidebarType);
  };

  const handleSidebarClose = () => {
    setActiveSidebar(null);
    fetchProfile(); // Refresh profile data
  };

  const formatPrice = (price: number | string | null) => {
    if (!price) return 'Not set';
    // Convert to number if it's a string (API might return string instead of number)
    const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numericPrice)) return 'Not set';
    return `$${numericPrice.toFixed(2)}`;
  };

  const formatDuration = (duration: number | null) => {
    if (!duration) return 'Not set';
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${minutes}m`;
    }
  };

  if (user?.role !== 'CAREGIVER') {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          Access denied. This page is only available for caregivers.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!profile) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="warning">Profile not found.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* FEAT-045 — Continue sign-up banner (MVP-8.10, MVP-8.11,
          MVP-18.17). Warm primary tone — never red error styling. The
          banner is hidden once isComplete flips to true so the surface
          quietly disappears at completion. */}
      {onboarding && !onboarding.isComplete && (
        <Card
          data-testid="caregiver-continue-signup-banner"
          sx={{
            mb: 3,
            borderLeft: '4px solid',
            borderColor: 'secondary.main',
            backgroundColor: 'secondary.light',
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <FavoriteIcon color="secondary" aria-hidden="true" />
              <Box sx={{ flex: 1, minWidth: 240 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Let's get you ready to help families
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  You're {onboarding.completionPercent}% of the way there. We'll
                  pick up wherever you left off.
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => navigate('/caregiver-sign-up')}
                data-testid="caregiver-continue-signup-cta"
              >
                Continue sign-up
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* FEAT-061 — Trust & Safety onboarding gate (MVP-8.20…8.27). Shown
          until the caregiver has acknowledged the current trust-and-safety
          basics. Warm primary tone, never red error styling. */}
      {trustSafetyAcknowledged === false && (
        <Card
          data-testid="trust-safety-gate-banner"
          sx={{
            mb: 3,
            borderLeft: '4px solid',
            borderColor: 'primary.main',
            backgroundColor: 'primary.light',
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <ShieldOutlinedIcon color="primary" aria-hidden="true" />
              <Box sx={{ flex: 1, minWidth: 240 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  A few trust & safety basics
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Please review and acknowledge these short reminders before you
                  start caring for families.
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/caregiver-trust-safety')}
                data-testid="trust-safety-gate-cta"
              >
                Review now
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar
            src={profile.avatar || undefined}
            sx={{ width: 64, height: 64, mr: 2 }}
          >
            {profile.firstName?.[0]}{profile.lastName?.[0]}
          </Avatar>
          <Box>
            <Typography variant="h4" component="h1">
              {profile.firstName} {profile.lastName}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Caregiver Profile Management
            </Typography>
          </Box>
        </Box>

        {/* FEAT-040 — Profile Completion Meter pulls the canonical
            percentage + breakdown from the API (MVP-8.4, MVP-18.15). */}
        <Box sx={{ mb: 3 }}>
          <ProfileCompletionMeter mode="caregiver" />
        </Box>
      </Box>

      {profile.id && <ProfileSummary caregiverId={profile.id} />}

      {/* Profile Sections */}
      <Grid container spacing={3}>
        {/* Bio Section */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PersonIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">Professional Bio</Typography>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => handleSidebarOpen('bio')}
                >
                  Edit
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {profile.bio ? (
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {profile.bio}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No bio added yet. Click Edit to add your professional bio.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Services Section */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <WorkIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">Services Offered</Typography>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => handleSidebarOpen('services')}
                >
                  Manage
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {profile.services.length > 0 ? (
                <Box>
                  {profile.services.slice(0, 3).map((service) => (
                    <Box key={service.id} sx={{ mb: 1 }}>
                      <Typography variant="subtitle2">{service.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatDuration(service.duration)} • {formatPrice(service.price)}
                      </Typography>
                    </Box>
                  ))}
                  {profile.services.length > 3 && (
                    <Typography variant="body2" color="text.secondary">
                      +{profile.services.length - 3} more services
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No services added yet. Click Manage to add your services.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Availability Section */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ScheduleIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">Availability</Typography>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => handleSidebarOpen('availability')}
                >
                  Set Schedule
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {profile.availability.length > 0 ? (
                <Box>
                  {profile.availability.slice(0, 3).map((slot) => (
                    <Box key={slot.id} sx={{ mb: 1 }}>
                      <Typography variant="subtitle2">
                        {slot.dayOfWeek}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {slot.startTime} - {slot.endTime}
                      </Typography>
                    </Box>
                  ))}
                  {profile.availability.length > 3 && (
                    <Typography variant="body2" color="text.secondary">
                      +{profile.availability.length - 3} more time slots
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No availability set yet. Click Set Schedule to add your availability.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Certifications Section */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <SchoolIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">Certifications</Typography>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => handleSidebarOpen('certifications')}
                >
                  Manage
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {profile.certifications.length > 0 ? (
                <Box>
                  {profile.certifications.slice(0, 3).map((cert) => (
                    <Box key={cert.id} sx={{ mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="subtitle2">{cert.name}</Typography>
                        {cert.verified && (
                          <Chip
                            label="Verified"
                            size="small"
                            color="success"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {cert.issuer}
                      </Typography>
                    </Box>
                  ))}
                  {profile.certifications.length > 3 && (
                    <Typography variant="body2" color="text.secondary">
                      +{profile.certifications.length - 3} more certifications
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No certifications added yet. Click Manage to add your certifications.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* FEAT-064 — Service Areas section (MVP-18.33). Caregivers select the
            zip-code areas they serve, gated behind a confirmation agreement. */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <MapOutlinedIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">Service Areas</Typography>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => navigate('/caregiver/service-areas')}
                  data-testid="service-areas-manage-btn"
                >
                  Manage
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Select the ZIP code areas you're willing to travel to. Your
                selections are gated by a confirmation agreement.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* FEAT-069 — Curated careseeker leads (MVP-18.11). */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PeopleOutlineIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">Careseeker Leads</Typography>
                </Box>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/caregiver/careseeker-leads')}
                  data-testid="careseeker-leads-manage-btn"
                >
                  View leads
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                See curated careseekers matched to your service areas — not an
                open directory browse.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* FEAT-065 — Premium area placement (MVP-18.7). */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <StarOutlineIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">Premium Placement</Typography>
                </Box>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/caregiver/premium-placement')}
                  data-testid="premium-placement-manage-btn"
                >
                  Manage
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Purchase premium visibility in the zip codes you serve.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* FEAT-047 — State agency register link (MVP-8.16, 8.35b).
          Visible when the caregiver's state has an active mapping and
          no license number is on file. The component itself renders
          nothing for unmapped states or once a license number exists. */}
      <Box sx={{ mt: 3 }} id="state-agency-register">
        <StateAgencyRegisterLink
          stateCode={profile.stateCode}
          licenseNumber={profile.stateLicenseNumber}
          surface="settings"
        />
      </Box>

      {/* FEAT-046 — Credential documents section (MVP-18.20, MVP-18.21).
          Caregivers upload PDFs/JPGs/PNGs/HEICs up to 25 MB; the panel
          renders their existing documents with warm review-status copy.
          Accepted documents power the Badge entitlements consumed by
          FEAT-039 on the public caregiver profile. */}
      <Box sx={{ mt: 3 }} id="credentials-upload">
        <CredentialDocumentUploader />
      </Box>

      {/* FEAT-080 — Skill claims with gated Advanced categories (MVP-18.56). */}
      <Box sx={{ mt: 3 }} id="skill-selector">
        <SkillSelector />
      </Box>

      {/* Sidebars */}
      <BioManagementSidebar
        open={activeSidebar === 'bio'}
        onClose={handleSidebarClose}
        currentBio={profile.bio}
      />
      <ServicesManagementSidebar
        open={activeSidebar === 'services'}
        onClose={handleSidebarClose}
        services={profile.services}
      />
      <AvailabilityManagementSidebar
        open={activeSidebar === 'availability'}
        onClose={handleSidebarClose}
        availability={profile.availability}
      />
      <CertificationsManagementSidebar
        open={activeSidebar === 'certifications'}
        onClose={handleSidebarClose}
        certifications={profile.certifications}
      />
    </Container>
  );
};

export default CaregiverProfileManagementPage;
